import React, { useCallback, useState } from "react";
import { graphql, useLazyLoadQuery, useMutation } from "react-relay";
import { LazyQueryNetworkBoundrary } from "../../../../../components/mobile/QueryNetworkBoundrary";

import { UserLoggedInNavigatorScreenProps } from "../../../../../TopLevelNavigators/UserLoggedIn/UserLoggedInNavigatorParamList";
import { WidgetScreen } from "../../common/WidgetScreen";

import { WidgetOverviewSettings } from "../WidgetOverviewSettings";

import { WATodoSummaryPreview } from "./WidgetTodoSummaryPreview";
import { withWebModal } from "@/src/TopLevelNavigators/UserLoggedIn/withWebModal";
import { WSTodoSummaryQuery } from "./__generated__/WSTodoSummaryQuery.graphql";
import { WSTodoSummaryMutation } from "./__generated__/WSTodoSummaryMutation.graphql";
import { useWidgetTodoSummaryPreviewQuery } from "./useWidgetTodoSummaryPreviewQuery";
import { WidgetScreenPreview } from "../WidgetScreenPreview/WidgetScreenPreview";

const WS_Todo_OVERVIEW_QUERY = graphql`
  query WSTodoSummaryQuery($id: ID!) {
    store {
      ...WidgetOverviewSettings_Store
    }
    widgetTodoSummary(id: $id) {
      id
      category {
        id
      }
      assignedTeams {
        id
      }
    }
  }
`;

export const WSTodoSummary = withWebModal(WSTodoSummaryC, true);
function WSTodoSummaryC(
  props: UserLoggedInNavigatorScreenProps<"WSTodoSummary">
) {
  return (
    <LazyQueryNetworkBoundrary>
      {(fetchKey) => <InnerWSTodoSummary {...props} fetchKey={fetchKey} />}
    </LazyQueryNetworkBoundrary>
  );
}

function InnerWSTodoSummary(
  props: UserLoggedInNavigatorScreenProps<"WSTodoSummary"> & {
    fetchKey: string;
  }
) {
  const data = useLazyLoadQuery<WSTodoSummaryQuery>(
    WS_Todo_OVERVIEW_QUERY,
    {
      id: props.route.params.widgetId,
    },
    { fetchKey: props.fetchKey, fetchPolicy: "store-and-network" }
  );

  const [teamIds, setTeamIds] = useState<string[]>(
    data.widgetTodoSummary?.assignedTeams.map(({ id }) => id) ?? []
  );

  const [save, loading] = useMutation<WSTodoSummaryMutation>(graphql`
    mutation WSTodoSummaryMutation(
      $storecastId: ID!
      $widgetId: ID!
      $update: WidgetTodoSummaryUpdateInput!
    ) {
      dashboard(dashboardId: $storecastId) {
        updateWidgetTodoSummary(id: $widgetId, update: $update) {
          id

          assignedTeams {
            id
            name
          }
        }
      }
    }
  `);

  const onSave = useCallback(() => {
    save({
      variables: {
        storecastId: props.route.params.storecastId,
        update: {
          setTeamIds: teamIds,
        },
        widgetId: props.route.params.widgetId,
      },
      onCompleted: () => {
        props.navigation.goBack();
      },
    });
  }, [
    props.navigation,
    props.route.params.storecastId,
    props.route.params.widgetId,
    save,
    teamIds,
  ]);

  const query = useWidgetTodoSummaryPreviewQuery({
    teamIds,
    storecastId: props.route.params.storecastId,
    todoCategoryId: data.widgetTodoSummary!.category!.id,
  });

  return (
    <WidgetScreen
      settings={
        <WidgetOverviewSettings
          store={data.store}
          setTeamIds={setTeamIds}
          teamIds={teamIds}
        />
      }
      preview={
        <WidgetScreenPreview
          onSave={onSave}
          loading={loading}
          size={props.route.params.size}
          unitSize={props.route.params.unitSize}
        >
          {query ? <WATodoSummaryPreview query={query} /> : null}
        </WidgetScreenPreview>
      }
    />
  );
}
